import { css, Global } from '@emotion/core'
import ErrorBoundary from '@fs/zion-error-boundary'
import { NoticeLoading } from '@fs/zion-icon'
import { useNonReligiousMode } from '@fs/zion-permissions'
import Root from '@fs/zion-root'
import { AuthRoute, NotFound, Redirect, Route, Switch } from '@fs/zion-router'
import { AppLayout, colors } from '@fs/zion-ui'
import { useUser } from '@fs/zion-user'
import React, { useState } from 'react'
import campaignList from './campaignList'
import { redirects } from './campaignRedirects'
import { FullScreenContext } from './FullScreenContext'
import isEmbeddedMobile from './utils/isEmbeddedMobile'

const CampaignUsabilla = React.lazy(() => import('./components/common/CampaignUsabilla'))
const SubNav = React.lazy(() => import('./components/common/SubNav/SubNav'))
const CampaignOutageBanner = React.lazy(() => import('./components/common/CampaignOutageBanner/CampaignOutageBanner'))
const HomePage = React.lazy(() => import('./components/homepage/HomePage'))
const ZionCMSRoute = React.lazy(() => import('./ZionCMSRoute'))

const globalStyles = css`
  // Scroll smoothly to in-page anchors
  :root {
    scroll-behavior: smooth;
  }

  // Improve text wrapping of all H1s and their subheadings
  h1,
  h1 + p {
    text-wrap: balance;
  }
  // Prevent long dialog titles from being truncated
  [data-overlay-header] * {
    white-space: normal;
  }
  // Add sufficient contrast for focused state of expander headers
  button[aria-expanded][aria-controls]:focus-visible {
    outline: 3px solid ${colors.transparent.gray60};
    outline-offset: -3px; // Outline inside, so that ExpandableCard doesn’t clip it
  }
`

const center = css`
  position: fixed;
  top: 50%;
  inset-inline-start: 50%;
  transform: translate(-50%, -50%);
`

function LoadingSpinner(): JSX.Element {
  return (
    <div data-testid="spinner" css={center}>
      <NoticeLoading size="lg" />
    </div>
  )
}

// For kiosks, hide the header and footer if the hidehf query parameter is present or
// if the hidehf localStorage item is set to true. This is what is being done in
// in other discovery experiences.
function shouldHidehf(): boolean {
  try {
    const hasHidehfParam = window.location.search.includes('hidehf')
    const hasHidehfLocalStorage = window?.localStorage?.getItem('hidehf') === 'true'
    if (hasHidehfParam) {
      window?.localStorage?.setItem('hidehf', 'true')
    }
    return hasHidehfParam || hasHidehfLocalStorage
  } catch (error) {
    console.error(error)
    return false
  }
}

function App(): JSX.Element {
  const { signedIn, userLoading } = useUser()
  const [nonReligiousModeCheck, status, error] = useNonReligiousMode()
  const nonReligiousMode = status === 'loading' || error !== null || nonReligiousModeCheck
  const [fullScreen, setFullScreen] = useState(false)
  const [hidehf] = useState(() => shouldHidehf())

  if (userLoading) return <LoadingSpinner />
  return (
    <FullScreenContext.Provider value={setFullScreen}>
      <Root
        analytics
        fullScreen={fullScreen}
        header={{ hidden: hidehf }}
        footer={{ hidden: hidehf }}
        helpChatbot={!isEmbeddedMobile() && !hidehf}
      >
        <AppLayout fullWidth>
          <ErrorBoundary>
            <Global styles={globalStyles} />
            {/* As of February 2025, all campaign pages should be hidden for Russian patrons. The requirePersonalData prop will use the NotSubjectToRussiaDataLocalization Activator to ensure that. */}
            <Route path="*" requirePersonalData>
              {/* SubNav and tree switching are non-critical; silently ignore any errors (dev mode still shows stack trace) */}
              <ErrorBoundary FallbackComponent={() => null}>
                {signedIn && <SubNav />}
                <CampaignOutageBanner />
              </ErrorBoundary>

              <Switch>
                {/* Add any new campaigns to campaignList.js */}
                {campaignList.map((campaign) => {
                  const CampaignRoute = campaign.authRoute ? AuthRoute : Route
                  return nonReligiousMode && campaign.isReligious ? null : (
                    <CampaignRoute
                      key={campaign.path}
                      path={campaign.path}
                      component={signedIn ? campaign.component : campaign.loggedOutComponent || campaign.component}
                    />
                  )
                })}

                {/* Internal listing page */}
                {!nonReligiousMode && <Route exact path="/list" component={HomePage} />}
                {/* Redirect / to /list in dev mode for convenience */}
                {process.env.NODE_ENV === 'development' && <Redirect exact from="/" to="/list" />}

                {/* Add any new redirects to campaignRedirects.js */}
                {redirects.map(({ from, to, preservePath }) => (
                  <Route
                    key={from}
                    path={from}
                    // Manually redirecting instead of using zion-router's Redirect, so that `to` can be external
                    component={({ location }) => {
                      const finalTo = preservePath ? `${location.pathname.replace(from, to)}${location.search}` : to
                      window.location.replace(finalTo)
                      return null
                    }}
                  />
                ))}

                {/* Any /page* subpaths will route to Zion CMS. See https://beta.familysearch.org/fhsites for docs. */}
                <Route path="/page" component={ZionCMSRoute} />

                <Route component={NotFound} />
              </Switch>
              <CampaignUsabilla />
            </Route>
          </ErrorBoundary>
        </AppLayout>
      </Root>
    </FullScreenContext.Provider>
  )
}
export default App
